<template>
  <BittsLayout :is-page="true" class="sharing-settings-page">
    <div
      :class="{ 'sharing-settings-page__padded': isSharingSettingsPopulation }"
    >
      <RouterView />
    </div>
    <div v-if="!isSharingSettingsPopulation">
      <div
        class="sharing-settings-page__header"
        :class="{
          'sharing-settings-page__header--flush': isShowingPartnersTab,
        }"
      >
        <PageTitle
          title="Sharing Settings"
          subtitle="Manage sharing settings for all your Populations and Partners — view defaults, customize sharing, and apply changes in bulk"
          class="mb-20"
        >
          <template v-if="showHeaderActions && canEdit" #action>
            <div class="flex items-center gap-8">
              <BittsButton
                v-if="hasPartnerTrackerAccess"
                text="Requests"
                type="neutral"
                variant="outline"
                size="small"
                class="text-14"
                :left-icon="['far', 'inbox']"
                left-icon-classes="pr-8"
                data-testid="open-requests-drawer"
                @click="handleOpenRequests"
              >
                <template #text-extra>
                  <BittsCount
                    v-if="requestsDrawerRef?.badgeCount"
                    :count="requestsDrawerRef?.badgeCount ?? 0"
                    class="ml-8"
                  />
                </template>
              </BittsButton>
              <BittsButton
                v-if="!isShowingPartnersTab"
                text="Save"
                :disabled="!hasChanges"
                :loading="isSaving"
                @click="handleSave"
                size="small"
              />
            </div>
          </template>
        </PageTitle>
        <SharingSettingsByPopulationsTab
          v-if="!isShowingPartnersTab"
          ref="populationsTabRef"
          :hide-title="hasByPartnersAccess"
        >
          <template v-if="hasByPartnersAccess" #prepend>
            <BittsSegment
              :tabs="segmentTabs"
              :active-index="activeSegmentIndex"
              @change:segment="onSegmentChange"
            />
          </template>
        </SharingSettingsByPopulationsTab>
      </div>
      <SharingSettingsByPartnersTab
        v-if="isShowingPartnersTab"
        ref="partnersTabRef"
      >
        <template v-if="hasByPartnersAccess" #prepend>
          <BittsSegment
            :tabs="segmentTabs"
            :active-index="activeSegmentIndex"
            class="w-fit"
            @change:segment="onSegmentChange"
          />
        </template>
        <template #actions>
          <PartnerTabBulkActions
            v-if="canEdit"
            :selected-count="partnersSelectedCount"
            @edit-tags="openEditTagsModal"
          />
        </template>
      </SharingSettingsByPartnersTab>
    </div>
  </BittsLayout>
  <RequestsDrawer
    :key="drawerCloseRouteName"
    v-if="hasPartnerTrackerAccess"
    ref="requestsDrawerRef"
    open-route-name="sharing_settings_requests"
    :close-route-name="drawerCloseRouteName"
    accept-route-name="sharing_settings_requests_accept_proposal"
    default-view="data_share"
  />
  <PartnerTabEditTagsModal
    v-if="editTagsPartners"
    :partners="editTagsPartners"
    :is-saving="isSavingTags"
    @save="handleSaveTags"
    @closed="closeEditTagsModal"
  />
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCount,
  BittsLayout,
  BittsSegment,
} from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, ref, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import RequestsDrawer from '@/components/dash/RequestsDrawer.vue';
import PageTitle from '@/components/PageTitle.vue';
import type { TagEditType } from '@/components/partners/bulkTagEdit';
import PartnerTabBulkActions from '@/components/sharing-settings/partner-tab/components/PartnerTabBulkActions.vue';
import PartnerTabEditTagsModal from '@/components/sharing-settings/partner-tab/components/PartnerTabEditTagsModal.vue';
import { saveBulkTagEdit } from '@/components/sharing-settings/partner-tab/saveBulkTagEdit';
import SharingSettingsByPartnersTab from '@/components/sharing-settings/partner-tab/SharingSettingsByPartnersTab.vue';
import type { SharingPartnerRow } from '@/components/sharing-settings/partner-tab/types';
import SharingSettingsByPopulationsTab from '@/components/sharing-settings/population-tab/SharingSettingsByPopulationsTab.vue';

import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import {
  TEMP_PARTNER_TRACKER_ACCESS,
  TEMP_SHARING_SETTINGS_BY_PARTNERS,
} from '@/constants/feature_flags';
import { useFeatureFlagStore } from '@/stores';

useHead({
  title: 'Sharing Settings - Crossbeam',
});

const route = useRoute();
const router = useRouter();
const { hasFeatureFlag } = useFeatureFlagStore();

const hasPartnerTrackerAccess = computed(() =>
  hasFeatureFlag(TEMP_PARTNER_TRACKER_ACCESS),
);

const hasByPartnersAccess = computed(() =>
  hasFeatureFlag(TEMP_SHARING_SETTINGS_BY_PARTNERS),
);

const isByPartnersTab = computed(
  () => route.name === 'sharing-settings-by-partners',
);

const isOnRequestsRoute = computed(
  () =>
    route.name === 'sharing_settings_requests' ||
    route.name === 'sharing_settings_requests_accept_proposal',
);

const wasOnPartnersTab = ref(false);

const isShowingPartnersTab = computed(() => {
  if (isOnRequestsRoute.value) return wasOnPartnersTab.value;
  return isByPartnersTab.value;
});

const isSharingSettingsPopulation = computed(
  () => route.name === 'sharing-settings-by-population',
);

/* ---- Segment tabs ---- */

const SEGMENT_BY_POPULATIONS = 'by-populations';
const SEGMENT_BY_PARTNERS = 'by-partners';

const segmentTabs = [
  { id: SEGMENT_BY_POPULATIONS, title: 'By Populations' },
  { id: SEGMENT_BY_PARTNERS, title: 'By Partners' },
];

const activeSegmentIndex = computed(() => (isShowingPartnersTab.value ? 1 : 0));

function onSegmentChange(id: string | number) {
  if (id === SEGMENT_BY_PARTNERS) {
    router.push({ name: 'sharing-settings-by-partners' });
  } else {
    router.push({ name: 'sharing-settings-by-populations' });
  }
}

const drawerCloseRouteName = computed(() => {
  if (isShowingPartnersTab.value) return 'sharing-settings-by-partners';
  if (hasByPartnersAccess.value) return 'sharing-settings-by-populations';
  return 'sharing-settings';
});

function handleOpenRequests() {
  wasOnPartnersTab.value = isByPartnersTab.value;
  requestsDrawerRef.value?.open();
}

/* ---- Tab refs ---- */

const requestsDrawerRef = ref<{ badgeCount: number; open: () => void } | null>(
  null,
);

const populationsTabRef = ref<InstanceType<
  typeof SharingSettingsByPopulationsTab
> | null>(null);

const partnersTabRef = ref<InstanceType<
  typeof SharingSettingsByPartnersTab
> | null>(null);

const hasChanges = computed(() => {
  if (isShowingPartnersTab.value)
    return partnersTabRef.value?.hasAnyChanges ?? false;
  return populationsTabRef.value?.hasAnyChanges ?? false;
});

const partnersSelectedCount = computed(
  () => partnersTabRef.value?.selectedRows?.length ?? 0,
);

const isSaving = computed(() => {
  if (isShowingPartnersTab.value)
    return partnersTabRef.value?.isSaving ?? false;
  return populationsTabRef.value?.isSaving ?? false;
});

const editTagsPartners = shallowRef<SharingPartnerRow[] | null>(null);

function openEditTagsModal() {
  editTagsPartners.value = [...(partnersTabRef.value?.selectedRows ?? [])];
}

function closeEditTagsModal() {
  editTagsPartners.value = null;
}

const isSavingTags = ref(false);

async function handleSaveTags(edit: TagEditType) {
  isSavingTags.value = true;
  try {
    const isSaved = await saveBulkTagEdit(editTagsPartners.value ?? [], edit);
    if (!isSaved) return;
    partnersTabRef.value?.clearSelection();
    closeEditTagsModal();
  } finally {
    isSavingTags.value = false;
  }
}

const hasPopulations = computed(
  () => populationsTabRef.value?.hasPopulations ?? false,
);

/* Show header actions when populations tab has populations, or when on partners tab */
const showHeaderActions = computed(() => {
  if (isShowingPartnersTab.value) return true;
  return hasPopulations.value;
});

const { canEdit } = useDataSharingPermissions();

async function handleSave() {
  if (isShowingPartnersTab.value) {
    await partnersTabRef.value?.saveChanges();
  } else {
    await populationsTabRef.value?.saveChanges();
  }
}
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";

/*
 * Remove BittsLayout page padding so the By Partners table can go edge-to-edge.
 * Every other region re-adds it, since the layout no longer supplies any.
 */
.sharing-settings-page.bitts-layout {
  @apply !p-0;
}

.sharing-settings-page__header {
  @apply px-24 pt-24 pb-12;
}

/* The By Partners toolbar carries its own spacing, so the header must not add
   a second gap above it. */
.sharing-settings-page__header--flush {
  @apply pb-0;
}

.sharing-settings-page__padded {
  @apply px-24 py-24;
}
</style>
